<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ $t('legacy.Выбрать фон') }}</h3>
        <button @click="$emit('close')" class="btn-close">×</button>
      </div>

      <div class="tabs">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
        >
          {{ $t(tab.label) }}
        </button>
      </div>

      <div class="tab-content">
        <div v-if="cropSource" class="crop-section">
          <Cropper
            :key="cropSource"
            ref="cropper"
            class="cropper"
            :src="cropSource"
            :stencil-props="{ aspectRatio: 4/3 }"
            @ready="cropReady = true"
            @error="handleCropError"
          />
          <div class="crop-actions">
            <button @click="cancelCrop" class="btn-secondary">{{ $t('common.cancel') }}</button>
            <button @click="applyCroppedImage" :disabled="!cropReady" class="btn-primary">
              {{ activeTab === 'library' ? $t('legacy.Применить фон') : $t('legacy.Применить') }}
            </button>
          </div>
        </div>
        <!-- Таб 1: Загрузка своего изображения -->
        <div v-if="activeTab === 'upload' && !cropSource" class="tab-panel">
          <div v-if="!uploadedImage" class="upload-section">
            <input 
              ref="fileInput"
              type="file" 
              accept="image/*"
              @change="handleFileUpload"
              style="display: none"
            />
            <div class="upload-placeholder" @click="$refs.fileInput.click()">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <p>{{ $t('legacy.Нажмите для загрузки изображения') }}</p>
            </div>
          </div>
          
        </div>

        <!-- Таб 2: Выбор цвета -->
        <div v-if="activeTab === 'color'" class="tab-panel">
          <div class="color-picker-section">
            <AppColorPicker
              v-model="selectedColor"
              :presets="presetColors"
            />

            <button @click="applyColorBackground" class="btn-primary apply-color-btn">
              {{ $t('legacy.Применить цвет') }}
            </button>
          </div>
        </div>

        <!-- Таб 3: База изображений -->
        <div v-if="activeTab === 'library' && !cropSource" class="tab-panel">
          <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>
          
          <div v-else-if="libraryBackgrounds.length === 0" class="empty-state">
            <p>{{ $t('Нет доступных фонов. Администратор еще не добавил фоны в библиотеку.') }}</p>
          </div>

          <div v-else class="library-grid">
            <div 
              v-for="bg in libraryBackgrounds" 
              :key="bg.id"
              @click="selectLibraryBackground(bg)"
              :class="['library-item', { selected: selectedLibraryBg === bg.id }]"
            >
              <img :src="bg.image_path" :alt="bg.name" loading="lazy" />
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { backgroundsApi } from '@/api/backgrounds'
import AppColorPicker from '@/components/AppColorPicker.vue'
import { getCache, setCache, CacheKeys } from '@/utils/cache'

const BACKGROUNDS_TTL = 30 * 60 * 1000; // 30 minutes

export default {
  name: 'BackgroundModal',
  components: {
    Cropper,
    AppColorPicker
  },
  computed: {
    cropSource() {
      if (this.activeTab === 'upload') return this.uploadedImage;
      if (this.activeTab === 'library') return this.libraryCropImage;
      return null;
    },
    isMobile() {
      return window.innerWidth <= 768;
    }
  },
  data() {
    return {
      activeTab: 'upload',
      tabs: [
        { id: 'upload', label: 'common.upload' },
        { id: 'color', label: 'editor.color' },
        { id: 'library', label: 'legacy.База изображений' }
      ],
      uploadedImage: null,
      libraryCropImage: null,
      cropReady: false,
      selectedColor: '#ffffff',
      presetColors: [
        '#ffffff', '#000000', '#e74c3c', '#3498db', '#2ecc71', 
        '#f39c12', '#9b59b6', '#1abc9c', '#34495e', '#95a5a6',
        '#ff6b6b', '#4ecdc4', '#ffe66d', '#a8e6cf', '#ff8b94'
      ],
      libraryBackgrounds: [],
      selectedLibraryBg: null,
      loading: false
    }
  },
  watch: {
    activeTab(newTab) {
      this.cropReady = false;
      if (newTab === 'library' && this.libraryBackgrounds.length === 0) {
        this.loadLibraryBackgrounds()
      }
    }
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.cropReady = false;
          this.uploadedImage = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    resetUpload() {
      this.cropReady = false
      this.uploadedImage = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    cancelCrop() {
      this.cropReady = false
      if (this.activeTab === 'library') {
        this.libraryCropImage = null
        this.selectedLibraryBg = null
      } else {
        this.resetUpload()
      }
    },

    handleCropError() {
      this.cropReady = false
      this.$notify({
        type: 'error',
        title: this.$t('common.error'),
        text: this.$t('Не удалось обработать изображение')
      })
    },

    async applyCroppedImage() {
      if (!this.cropReady || !this.$refs.cropper) {
        this.$notify({
          type: 'error',
          title: this.$t('common.error'),
          text: this.$t('Кадрировщик не инициализирован')
        })
        return
      }

      try {
        const result = this.$refs.cropper.getResult()
        if (!result || !result.canvas) {
          this.handleCropError()
          return
        }

        const dataUrl = result.canvas.toDataURL('image/jpeg', 0.9)
        if (!dataUrl.startsWith('data:image/')) {
          this.handleCropError()
          return
        }
        this.$emit('apply-background', {
          type: this.activeTab === 'library' ? 'library' : 'custom',
          value: dataUrl
        })
        this.$emit('close')
      } catch {
        // A failed load or a cross-origin canvas must never replace the zone's background.
        this.handleCropError()
      }
    },

    applyColorBackground() {
      this.$emit('apply-background', {
        type: 'color',
        value: this.selectedColor
      })
      this.$emit('close')
    },

    async loadLibraryBackgrounds() {
      const cachedBackgrounds = getCache(CacheKeys.BACKGROUNDS);
      if (cachedBackgrounds) {
        this.libraryBackgrounds = cachedBackgrounds;
        return;
      }

      this.loading = true
      try {
        const response = await backgroundsApi.getAll()
        if (response.data.success) {
          this.libraryBackgrounds = response.data.backgrounds;
          setCache(CacheKeys.BACKGROUNDS, response.data.backgrounds, BACKGROUNDS_TTL);
        }
      } catch (error) {
        console.error('Failed to load backgrounds:', error)
        this.$notify({
          type: 'error',
          title: this.$t('common.error'),
          text: this.$t('Не удалось загрузить фоны')
        })
      } finally {
        this.loading = false
      }
    },

    selectLibraryBackground(bg) {
      if (!bg.image_path) {
        this.handleCropError()
        return
      }
      this.cropReady = false
      this.selectedLibraryBg = bg.id
      this.libraryCropImage = bg.image_path
    }
  }
}
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
}

.btn-close {
  background: none;
  border: none;
  font-size: 32px;
  color: #666;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;

  &:hover {
    color: #333;
  }
}

.tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
}

.tab-button {
  flex: 1;
  padding: 16px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  transition: all 0.2s;

  &:hover {
    color: #333;
    background: #f5f5f5;
  }

  &.active {
    color: #2196F3;
    border-bottom-color: #2196F3;
    background: #f5f9ff;
  }
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.tab-panel {
  animation: fadeIn 0.2s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.upload-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.upload-placeholder {
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 60px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  max-width: 400px;

  &:hover {
    border-color: #2196F3;
    background: #f5f9ff;
  }

  svg {
    color: #2196F3;
    margin-bottom: 16px;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 16px;
  }
}

.crop-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cropper {
  height: 400px;
  background: #f5f5f5;
  border-radius: 8px;
}

.crop-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.color-picker-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  max-width: 320px;
  margin: 0 auto;
}

.apply-color-btn {
  width: 100%;
  margin-top: 8px;
}

.library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.library-item {
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.2s;
  background: #f5f5f5;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    border-color: #2196F3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.2);
  }

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
  }
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
}

.btn-primary, .btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: #2196F3;
  color: white;
  width: 100%;

  &:hover:not(:disabled) {
    background: #1976D2;
  }
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;

  &:hover {
    background: #e0e0e0;
  }
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 90vh;
    position: relative;
  }

  .tab-content {
    padding: 16px;
  }

  .upload-placeholder {
    padding: 40px 20px;
  }

  .cropper {
    height: 300px;
  }

  .library-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  .preset-colors {
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  }
}
</style>
